import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import TitleWithBack from '../../components/common/TitleWithBack';
import { ProgressBar } from 'react-bootstrap';
import { downloadBulkUploadTemplate } from '../../utils/bulkUploadTemplates';

function ModulesCreate() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    moduleName: '',
    moduleCode: '',
    examType: '',
    program: '',
    yearOfStudy: '',
    semester: '',
    requiredQuestionsCount: '',
    maxScore: ''
  });
  const [isUploading, setIsUploading] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [tooltip, setTooltip] = useState('');
  const [activeTab, setActiveTab] = useState('single');
  const [uploadProgress, setUploadProgress] = useState(0);

  const [programs, setPrograms] = useState([]);
  const [examTypes, setExamTypes] = useState([]);
  const [yearsOfStudy, setYearsOfStudy] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [modular, setModular] = useState(false);

  useEffect(() => {
    async function fetchDropdowns() {
      try {
        const [progRes, examTypeRes, yearRes, semRes] = await Promise.all([
          axios.get('/api/programs'),
          axios.get('/api/exam-types'),
          axios.get('/api/years-of-study'),
          axios.get('/api/semesters')
        ]);
        setPrograms(Array.isArray(progRes.data) ? progRes.data : (progRes.data.data || []));
        setExamTypes(Array.isArray(examTypeRes.data) ? examTypeRes.data : (examTypeRes.data.data || []));
        setYearsOfStudy(Array.isArray(yearRes.data) ? yearRes.data : (yearRes.data.data || []));
        setSemesters(Array.isArray(semRes.data) ? semRes.data : (semRes.data.data || []));
      } catch (err) {
        console.error('Error fetching dropdown data', err);
      }
    }
    fetchDropdowns();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleProgramChange = (e) => {
    const value = e.target.value;
    setForm(prev => ({ ...prev, program: value }));
    const selected = programs.find(p => String(p.id) === String(value));
    setModular(selected?.structureType === 'MODULAR');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (uploadFile) {
      const formData = new FormData();
      formData.append('file', uploadFile);
      setIsUploading(true);
      setUploadProgress(0);
      try {
        await axios.post('/api/bulk-upload/modules', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percent);
          }
        });
        setUploadProgress(100);
        setTooltip('Modules uploaded successfully!');
        setTimeout(() => navigate('/admin/modules/view'), 2000);
      } catch (err) {
        let backendMessage = 'Unknown error occurred while uploading modules.';
      if (err.response && err.response.data) {
        if (typeof err.response.data === 'string') {
          backendMessage = err.response.data;
        } else if (typeof err.response.data.message === 'string') {
          backendMessage = err.response.data.message;
        }
      }
      setIsUploading(false);
      setTooltip(`Error: ${backendMessage}`);
      }
    } else {
      const selectedExamType = examTypes.find(et => et.id === parseInt(form.examType, 10));
      const isScoringGuideType = selectedExamType?.name === 'RLP' || selectedExamType?.name === 'OSM';
      const selectedProgram = programs.find(p => String(p.id) === String(form.program));
      const payload = {
        moduleName: form.moduleName,
        moduleCode: form.moduleCode,
        examType: { id: parseInt(form.examType, 10) },
        program: { id: parseInt(form.program, 10) },
        yearOfStudy: modular ? null : { id: parseInt(form.yearOfStudy, 10) },
        semester: modular ? null : { id: parseInt(form.semester, 10) },
        requiredQuestionsCount: isScoringGuideType && form.requiredQuestionsCount ? parseInt(form.requiredQuestionsCount, 10) : null,
        maxScore: isScoringGuideType && form.maxScore ? parseInt(form.maxScore, 10) : null
      };
      try {
        if (selectedProgram && modular !== (selectedProgram.structureType === 'MODULAR')) {
          await axios.put(`/api/programs/${form.program}/structure-type`, {
            structureType: modular ? 'MODULAR' : 'SEMESTER'
          });
        }
        await axios.post('/api/modules', payload);
        setTooltip('Module created successfully!');
        setTimeout(() => navigate('/admin/modules/view'), 2000);
      } catch (err) {
        setTooltip('Error creating module.');
      }
    }
  };

  const selectedExamTypeName = examTypes.find(et => et.id === parseInt(form.examType, 10))?.name;
  const isScoringGuideExamType = selectedExamTypeName === 'RLP' || selectedExamTypeName === 'OSM';

  return (
    <div className="container mt-2">
      <TitleWithBack title="Create New Module" />
      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button 
            type="button"
            className={`nav-link ${activeTab === 'single' ? 'active' : ''}`}
            onClick={() => { setActiveTab('single'); setUploadFile(null); }}
          >
            Single Create
          </button>
        </li>
        <li className="nav-item">
          <button 
            type="button"
            className={`nav-link ${activeTab === 'bulk' ? 'active' : ''}`}
            onClick={() => setActiveTab('bulk')}
          >
            Bulk Upload
          </button>
        </li>
      </ul>
      {activeTab === 'single' && (
        <div className="card shadow-sm">
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label className="form-label">Module Name</label>
                <input 
                  type="text"
                  className="form-control"
                  name="moduleName"
                  value={form.moduleName}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Module Code</label>
                <input 
                  type="text"
                  className="form-control"
                  name="moduleCode"
                  value={form.moduleCode}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Exam Type</label>
                <select 
                  className="form-select"
                  name="examType"
                  value={form.examType}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Exam Type</option>
                  {examTypes.map(et => (
                    <option key={et.id} value={et.id}>
                      {et.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-3">
                <label className="form-label">Program</label>
                <select
                  className="form-select"
                  name="program"
                  value={form.program}
                  onChange={handleProgramChange}
                  required
                >
                  <option value="">Select Program</option>
                  {programs.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-3 form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="modularCheck"
                  checked={modular}
                  onChange={(e) => setModular(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="modularCheck">
                  This program is Modular (no Year of Study / Semester)
                </label>
                <div className="form-text">This applies to the entire program, not just this module.</div>
              </div>
              {!modular && (
                <>
                  <div className="mb-3">
                    <label className="form-label">Year of Study</label>
                    <select
                      className="form-select"
                      name="yearOfStudy"
                      value={form.yearOfStudy}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select Year of Study</option>
                      {yearsOfStudy.map(y => (
                        <option key={y.id} value={y.id}>
                          {y.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Semester</label>
                    <select
                      className="form-select"
                      name="semester"
                      value={form.semester}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select Semester</option>
                      {semesters.map(s => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}
              {isScoringGuideExamType && (
                <>
                  <div className="mb-3">
                    <label className="form-label">Questions a Candidate Must Attempt</label>
                    <input
                      type="number"
                      min="1"
                      className="form-control"
                      name="requiredQuestionsCount"
                      value={form.requiredQuestionsCount}
                      onChange={handleChange}
                      placeholder="Leave blank to require every question on the scoring guide"
                    />
                    <div className="form-text">
                      For RLP/OSM modules where a candidate isn't required to attempt every question. Leave blank
                      if every question on the scoring guide is mandatory.
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Maximum Score</label>
                    <input
                      type="number"
                      min="1"
                      className="form-control"
                      name="maxScore"
                      value={form.maxScore}
                      onChange={handleChange}
                      placeholder="Leave blank to use the scoring guide's own total"
                    />
                    <div className="form-text">
                      The fixed total used to calculate a candidate's final percentage, independent of how many
                      questions the scoring guide has. Leave blank to use the sum of the scoring guide's max
                      scores instead.
                    </div>
                  </div>
                </>
              )}
              <button type="submit" className="btn btn-primary">Create Module</button>
            </form>
          </div>
        </div>
      )}
      {activeTab === 'bulk' && (
        <div className="card shadow-sm">
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => downloadBulkUploadTemplate('modules')}
                >
                  Download Template
                </button>
              </div>
              <div className="mb-3">
                <label className="form-label">Select Excel File</label>
                <input 
                  type="file"
                  className="form-control"
                  accept=".xls,.xlsx"
                  onChange={(e) => {
                    setUploadFile(e.target.files[0]);
                    setTooltip('');
                    setUploadProgress(0);
                    setIsUploading(false);
                  }}
                />
              </div>
              {uploadFile && (
                <p className="fst-italic">File selected: {uploadFile.name}</p>
              )}
              {isUploading && (
               <div className="my-3">
                <ProgressBar 
                now={uploadProgress} 
                label={`${uploadProgress}%`} 
                animated 
                variant={uploadProgress < 100 ? 'info' : 'success'} 
               />
              </div>
             )}
              <button type="submit" className="btn btn-primary">Upload Modules</button>
            </form>
          </div>
        </div>
      )}
      {tooltip && (
        <div className={`mt-3 alert ${tooltip.startsWith('Error') ? 'alert-danger' : 'alert-success'}`}>
          {tooltip}
        </div>
      )}
    </div>
  );
}

export default ModulesCreate;
